import { useEffect, useState } from 'react';

/**
 * 📱 Track the horizontal scroll of the mobile map container.
 *
 * Returns the current scrollLeft of `[data-map-scroll-container]`. Used by
 * the bottom-left panel cluster (Intelligence Feed + Country Stats) so it
 * can apply `transform: translateX(-scrollLeft)` and stay anchored to the
 * left edge of the MAP — not the viewport.
 *
 * The effect: when the player scrolls right to look at Russia/China the
 * panels slide off-screen, freeing precious mobile real estate. When they
 * scroll back to USA, the panels reappear at their natural position.
 *
 * Also detects when mobile-portrait mode is active. Returns 0 + false on
 * desktop so consumers can render the panels normally.
 */
export default function useMapScrollLeft() {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsMobilePortrait(w < 768 && h > w);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  useEffect(() => {
    if (!isMobilePortrait) {
      setScrollLeft(0);
      return;
    }

    let el = null;
    let raf = 0;
    const findAndAttach = () => {
      el = document.querySelector('[data-map-scroll-container]');
      if (!el) {
        raf = requestAnimationFrame(findAndAttach);
        return;
      }
      const onScroll = () => setScrollLeft(el.scrollLeft);
      onScroll();
      el.addEventListener('scroll', onScroll, { passive: true });
      // Save the cleanup function on the ref
      cleanup = () => el.removeEventListener('scroll', onScroll);
    };

    let cleanup = null;
    findAndAttach();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (cleanup) cleanup();
    };
  }, [isMobilePortrait]);

  return { scrollLeft, isMobilePortrait };
}
